import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const sanitizeFileName = (name, fallback) =>
  String(name || fallback)
    .trim()
    .replace(/[^\w-]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;

const LOGO_BAND_ROWS = 2;
const LOGO_BAND_ROW_HEIGHT = 36;
const LOGO_BAND_FILL = "E9EEFB";
const LOGO_MAX_WIDTH = 170;
const LOGO_MAX_HEIGHT = 75;

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const getImageNaturalSize = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Failed to read logo dimensions"));
    img.src = dataUrl;
  });

/** xlsx has no native SVG support — rasterize to PNG via an offscreen canvas. */
const rasterizeSvgDataUrl = (svgDataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || LOGO_MAX_WIDTH;
      canvas.height = img.naturalHeight || LOGO_MAX_HEIGHT;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Failed to rasterize SVG logo"));
    img.src = svgDataUrl;
  });

/**
 * Resolves to `{ base64, extension, width, height }` — width/height are scaled down to
 * fit the logo band while preserving the logo's own aspect ratio (never stretched).
 * Resolves to `null` if the logo can't be fetched/embedded.
 */
const tryLoadCompanyLogoImage = async (logoUrl) => {
  try {
    const response = await fetch(logoUrl);
    if (!response.ok) throw new Error("Failed to fetch company logo");
    const blob = await response.blob();
    let base64 = await readBlobAsDataUrl(blob);
    let extension = (blob.type.split("/")[1] || "png").toLowerCase();

    if (extension.includes("svg")) {
      base64 = await rasterizeSvgDataUrl(base64);
      extension = "png";
    }

    const natural = await getImageNaturalSize(base64);
    const scale = Math.min(
      LOGO_MAX_WIDTH / natural.width,
      LOGO_MAX_HEIGHT / natural.height,
      1,
    );

    return {
      base64,
      extension: extension === "jpg" ? "jpeg" : extension,
      width: Math.round(natural.width * scale),
      height: Math.round(natural.height * scale),
    };
  } catch {
    // Missing/unreachable/unsupported logo — export still succeeds without branding.
    return null;
  }
};

/**
 * Adds a small filled band directly above the data table with the company logo in it.
 * Fixed row count regardless of how many columns are exported, so it always stays
 * compact. Must run before any other rows/merges are added — ExcelJS's spliceRows
 * doesn't shift already-declared merge ranges, so inserting the band later would leave
 * merged cells (e.g. the totals row) pointing at the wrong rows.
 */
const addCompanyLogoBand = (workbook, worksheet, { totalCols, logoAsset }) => {
  const bandRows = Array.from({ length: LOGO_BAND_ROWS }, () => worksheet.addRow([]));
  const firstRowNumber = bandRows[0].number;

  bandRows.forEach((row) => {
    row.height = LOGO_BAND_ROW_HEIGHT;
    for (let col = 1; col <= totalCols; col += 1) {
      row.getCell(col).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: LOGO_BAND_FILL },
      };
    }
  });

  const imageId = workbook.addImage(logoAsset);
  // Starts in column B (not A) so the sheet's default A1 selection box never lands on it.
  worksheet.addImage(imageId, {
    tl: { col: 1.15, row: firstRowNumber - 1 + 0.15 },
    ext: { width: logoAsset.width, height: logoAsset.height },
  });
};

/**
 * One applicant is one row, so every role they hold is listed in that single Role cell.
 * Roles are mapped per shift, so the same role name can repeat — dedupe before joining.
 */
const joinRoleNames = (roles = []) =>
  [...new Set(roles.map((role) => role.name).filter(Boolean))].join(", ");

const buildSingleRow = (data) => {
  const autoAmount =
    data.deductions.entries.find((entry) => entry.type === "auto")?.amount || 0;

  return {
    name: data.displayName,
    id: data.applicant.id,
    days: data.baseEarnings.totalDays,
    payrollDays: data.baseEarnings.totalDays,
    autoDeduction: autoAmount,
    manualDeduction: data.deductions.total - autoAmount,
    manualDeductionReason: data.deductions.entries
      .filter((entry) => entry.type === "manual")
      .map((entry) => entry.reason)
      .join(", "),
    manualDeductionAmount: data.deductions.total - autoAmount,
    reward: data.rewards.total,
    rewardReason: data.rewards.entries.map((entry) => entry.reason).join(", "),
    numberOfTrainingDays: data.training.totalDays,
    trainingPayPerDay: data.training.roles.map((role) => role.ratePerDay).join(", "),
    totalTrainingPay: data.training.total,
    totalAmount: data.summary.netPay,
    stcNumber: data.applicant.stcNumber,
    iqamaId: data.applicant.idNumber,
    training: data.training.total,
    transferCommission: "",
    signature: "",
    role: joinRoleNames(data.baseEarnings.roles),
  };
};

const buildBulkRows = (data) =>
  data.applicants.map((applicant) => ({
    name: applicant.displayName,
    id: applicant.id,
    days: applicant.days,
    payrollDays: applicant.days,
    autoDeduction: applicant.autoDeduction,
    manualDeduction: applicant.manualDeduction,
    manualDeductionReason: applicant.manualDeductionReason,
    manualDeductionAmount: applicant.manualDeduction,
    reward: applicant.rewards,
    rewardReason: applicant.rewardReason,
    numberOfTrainingDays: applicant.trainingDays,
    trainingPayPerDay: applicant.trainingRoles
      .map((role) => role.ratePerDay)
      .join(", "),
    totalTrainingPay: applicant.trainingPay,
    totalAmount: applicant.totalAmount,
    stcNumber: applicant.stcNumber,
    iqamaId: applicant.idNumber,
    training: applicant.trainingPay,
    transferCommission: "",
    signature: "",
    role: joinRoleNames(applicant.roles),
  }));

export const exportPayrollReport = async ({
  data,
  isSingle,
  selectedFields,
  fields,
  lang,
  eventName,
  summaryLabels,
  companyLogoUrl,
}) => {
  const rows = isSingle ? [buildSingleRow(data)] : buildBulkRows(data);
  const selectedFieldDefs = fields.filter((field) => selectedFields[field.name]);
  const wrapFieldNames = ["manualDeductionReason", "rewardReason", "role"];
  const wrapColumnIndexes = selectedFieldDefs
    .map((field, index) => (wrapFieldNames.includes(field.name) ? index + 1 : null))
    .filter(Boolean);

  const logoAsset = companyLogoUrl
    ? await tryLoadCompanyLogoImage(companyLogoUrl)
    : null;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("payroll", {
    // Explicit activeCell/topLeftCell so the file always opens scrolled to the top with a
    // known, harmless cell selected — otherwise viewers can default to whatever cell was
    // last written (e.g. a far-right column). A1 is safe to select because the logo starts
    // in column B (see addCompanyLogoBand), so the selection box never lands on top of it.
    views: [{ rightToLeft: lang === "ar", state: "normal", activeCell: "A1", topLeftCell: "A1" }],
  });

  // Column `key`/`width` only — `header` is omitted so it doesn't auto-write into row 1,
  // which would collide with the logo band below. The header row is added explicitly instead.
  worksheet.columns = selectedFieldDefs.map((field) => ({
    key: field.name,
    width: wrapFieldNames.includes(field.name)
      ? 45
      : Math.min(Math.max(String(field.label).length + 8, 16), 40),
  }));

  if (logoAsset) {
    addCompanyLogoBand(workbook, worksheet, {
      totalCols: selectedFieldDefs.length,
      logoAsset,
    });
  }

  const headerRow = worksheet.addRow(selectedFieldDefs.map((field) => field.label));
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9E1F2" },
  };

  const WRAP_COLUMN_CHARS_PER_LINE = 40;
  const WRAP_LINE_HEIGHT = 15;

  rows.forEach((row) => {
    const excelRow = worksheet.addRow(
      Object.fromEntries(
        selectedFieldDefs.map((field) => [field.name, row[field.name] ?? ""]),
      ),
    );
    excelRow.alignment = { vertical: "middle", horizontal: "center" };

    let maxLines = 1;
    wrapColumnIndexes.forEach((colIndex) => {
      excelRow.getCell(colIndex).alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      const field = selectedFieldDefs[colIndex - 1];
      const textLength = String(row[field.name] ?? "").length;
      const lines = Math.max(1, Math.ceil(textLength / WRAP_COLUMN_CHARS_PER_LINE));
      maxLines = Math.max(maxLines, lines);
    });
    if (maxLines > 1) {
      excelRow.height = maxLines * WRAP_LINE_HEIGHT + 5;
    }
  });

  const totals = isSingle
    ? {
        deductions: data.summary.deductions,
        rewards: data.summary.rewards,
        netPay: data.summary.netPay,
      }
    : {
        deductions: data.summary.totalDeductions,
        rewards: data.summary.totalRewards,
        netPay: data.summary.totalNetPay,
      };

  const formatSignedTotal = (value, sign) => {
    const abs = Math.abs(value || 0);
    return abs === 0 ? "0" : `${sign}${abs}`;
  };

  const styleTotalCell = (cell, color, fill) => {
    cell.font = { bold: true, size: 14, color: { argb: color } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  };

  // Force equal widths on the 6 columns the totals blocks span, so the
  // merged blocks come out the same size regardless of the data table's own
  // (label-length-based) column widths.
  for (let col = 1; col <= 6; col += 1) {
    worksheet.getColumn(col).width = 16;
  }

  const totalAmountLabel =
    fields.find((field) => field.name === "totalAmount")?.label || "Total Amount";

  worksheet.addRow([]);
  const totalsRow = worksheet.addRow([
    `${summaryLabels.totalDeductions}: ${formatSignedTotal(totals.deductions, "-")}`,
    "",
    "",
    `${summaryLabels.totalRewards}: ${formatSignedTotal(totals.rewards, "+")}`,
  ]);
  totalsRow.height = 30;
  worksheet.mergeCells(totalsRow.number, 1, totalsRow.number, 3);
  worksheet.mergeCells(totalsRow.number, 4, totalsRow.number, 6);
  styleTotalCell(totalsRow.getCell(1), "FFDC2626", "FFFEF2F2");
  styleTotalCell(totalsRow.getCell(4), "FF059669", "FFF0FDF4");

  const netPayRow = worksheet.addRow([`${totalAmountLabel}: ${totals.netPay}`]);
  netPayRow.height = 30;
  worksheet.mergeCells(netPayRow.number, 1, netPayRow.number, 6);
  styleTotalCell(netPayRow.getCell(1), "FF9645FF", "FFF5F3FF");

  const buffer = await workbook.xlsx.writeBuffer();
  const fileBase = sanitizeFileName(eventName, "payroll-report");
  saveAs(new Blob([buffer]), `${fileBase}.xlsx`);
};
